// A section, as the preview composer sees it.
//
// The composer under `@common/render` renders PAGES, and a section on its own is
// not one. It does not have to become one: a `code` element is the element type
// that carries its content inline, and what it carries — html, style, script,
// types, values, wrapped in a frame — is a section field for field. So a section
// previews as a page with exactly one code element in it, and the editor beside
// it needs no renderer, no context and no `data-edit` rewriting of its own.
//
// That is what the archived project did (`site-frontend`'s section editor built
// the same one-element page and handed it to the same frame), and it is the
// reason the two editors can share a preview pane at all.
//
// Both halves live here because they are inverses and have to agree: `previewPage`
// puts the section INTO the composer's shape, `sectionPath` takes a path the
// composer emitted back OUT of it. The index in the regex below is the index of
// the element built above it.

// The section as a one-element page.
//
// Memoise this on the section's own fields at the call site: it is what the
// preview frame watches for changes, so a fresh identity per render would
// reload the frame forever (see @repoflow/components/preview).
export const previewPage = (section = {}) => ({
  elements: [
    {
      type: "code",
      // The section's OWN tags are labels for finding it in a list and say
      // nothing about placement. Passing them through would let a section
      // tagged "page:header" render into the page header slot instead of the
      // body, which on a screen showing one section is a preview that appears
      // to have dropped it.
      tags: [],
      frame: section.frame,
      config: {
        html: section.html,
        style: section.style,
        script: section.script,
        types: section.types,
        values: section.values,
      },
    },
  ],
});

// `elements[0].config.` — what the composer prefixed a `data-edit` path with,
// and what has to come off before the path means anything to the section.
const ELEMENT_PATH = /^elements\[0\]\.config\.(.+)$/;

// The path INTO the section that an inline edit in the preview is about.
//
// The composer rewrites `data-edit="heading"` to an absolute path into the page
// it is composing — see editorMarkup in @common/render/template — because on a
// real page only the composer knows which element the markup came from. Here
// there is only ever one element, so the rewrite is a prefix and this takes it
// off again: `elements[0].config.values.heading` is the section's `values.heading`.
//
// `null` for anything else, and the caller ignores it rather than writing to a
// path it does not recognise. Nothing should send one — but the frame is a
// message boundary, and a change to the composer's rewriting would otherwise
// arrive here as a silent write to the wrong field.
export const sectionPath = (datapath) => {
  const matched = ELEMENT_PATH.exec(datapath || "");

  return matched ? matched[1] : null;
};
